/**
 * Stream view controls — rendered as an overlay on the video stream.
 *
 * Desktop: persistent bottom controls (fullscreen + end stream).
 * Mobile: header is hidden and stream fills viewport automatically.
 */

import { useCallback, useRef } from "react";
import type { GameLiftStreams } from "../lib/gamelift-streams-websdk/gameliftstreams-1.1.0.mjs";
import { isMobile } from "../lib/isMobile";
import { VirtualGamepad } from "./VirtualGamepad";
import { LuArrowLeft, LuFullscreen, LuX } from "react-icons/lu";

interface StreamViewProps {
  glsInstance: GameLiftStreams;
  onEndStream: () => void;
}

export function StreamView({ glsInstance, onEndStream }: StreamViewProps) {
  const endStreamMobileButtonRef = useRef<HTMLButtonElement>(null);

  // Helper to determine if the device orientation is horizontal / portrait
  const isLandscape = useCallback(() => {
    if (typeof window === "undefined") return false;

    return window.matchMedia("(orientation: landscape)").matches;
  }, []);

  const toggleFullscreen = useCallback(async () => {
    const container = document.querySelector(".stream-view");
    const orientation = screen.orientation as ScreenOrientation & {
      lock?: (orientation: "landscape" | "portrait" | "any" | "natural") => Promise<void>;
      unlock?: () => void;
    };
    if (!container) return;

    if (document.fullscreenElement) {
      await document.exitFullscreen();
      try {
        orientation.unlock?.();
      } catch {
        // orientation.unlock() is not supported in all browsers
      }
    } else {
      await container.requestFullscreen();
      try {
        await orientation.lock?.("landscape");
      } catch {
        // Orientation lock typically requires fullscreen and is not supported everywhere
      }
      try {
        await (navigator as any).keyboard?.lock(["Escape"]);
      } catch {
        // keyboard.lock() not supported in all browsers — graceful degradation
      }
    }
  }, []);

  // NOTE: iOS on iPhone doesn't support fullscreen toggle or orientation lock
  // For these devices, assume horizontal mode == fullscreen game mode
  const isIPhoneIOS = /iPhone|iPod/i.test(window.navigator.userAgent);

  return (
    <>
    <div className="stream-controls">
      <button onClick={toggleFullscreen} className="btn-control">
        Fullscreen
      </button>
      <button onClick={onEndStream} className="btn-control">
        End Stream
      </button>
    </div>
    
    {isMobile() && (
      <>
        {isLandscape() ? (
          // Display the virtual gamepad only in landscape mode 
          <VirtualGamepad glsInstance={glsInstance} />
        ) : (
          // Display message to rotate device to landscape mode
          <div className="pp-rotate-overlay">
            <div className="pp-rotate-box">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#c84b00" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 2v6h-6"/><path d="M3 12a9 9 0 0 1 15-6.7L21 8"/>
                <path d="M3 22v-6h6"/><path d="M21 12a9 9 0 0 1-15 6.7L3 16"/>
              </svg>
              <div className="pp-rotate-text">Rotate to landscape<br />mode to use gamepad.</div>
            </div>
          </div>
        )}
        
        {!isIPhoneIOS && (
          // Toggle fullscreen button available on non-iPhone iOS devices
          <button className="btn-icon-mobile btn-fullscreen-mobile" onClick={toggleFullscreen}>
            {document.fullscreenElement ? <LuArrowLeft /> : <LuFullscreen />}
          </button>
        )}
        
        {!document.fullscreenElement &&
          // Display end stream button when not in fullscreen mode
          (<button className="btn-icon-mobile btn-end-mobile" onClick={onEndStream} ref={endStreamMobileButtonRef}>
            <LuX />
          </button>
        )}
      </>
    )}
    </>
  );
}
